import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Zap, BarChart2, TrendingUp, Layers, LayoutGrid, LayoutDashboard,
  CalendarCheck, Timer, Target, Building2, Users, Activity,
  SlidersHorizontal, Flag, PieChart as LuPie, Search, ChevronDown, Check, X,
  User, ShieldCheck
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area, ResponsiveContainer
} from "recharts";

import { C, KPIS_BASE, aT } from "../components/Dashboard/constants";
import KPICard        from "../components/Dashboard/KPICard";
import GaugeSVG       from "../components/Dashboard/GaugeSVG";
import ChartTooltip   from "../components/Dashboard/ChartTooltip";
import Card           from "../components/Dashboard/Card";
import { Donut, Leg, MiniLeg } from "../components/Dashboard/Donut";
import ScrollChart    from "../components/Dashboard/ScrollChart";
import Bdg            from "../components/Dashboard/Bdg";
import FilterSidebar  from "../components/Dashboard/FilterSidebar";
import InProgressPanel      from "../components/Dashboard/InProgressPanel";
import CompletedPanel       from "../components/Dashboard/CompletedPanel";
import OpenTasksPanel       from "../components/Dashboard/OpenTasksPanel";
import OnHoldPanel          from "../components/Dashboard/OnHoldPanel";
import PendingApprovalPanel from "../components/Dashboard/PendingApprovalPanel";
import OverduePanel         from "../components/Dashboard/OverduePanel";
import ClosedTasksPanel     from "../components/Dashboard/ClosedTasksPanel";
import ActiveUsersPanel     from "../components/Dashboard/ActiveUsersPanel";
import TaskListDetailPanel  from "../components/Dashboard/TaskListDetailPanel";
import axiosInstance, { getUserRole } from "../API/interceptor";

// ─── Constants ───────────────────────────────────────────────────────────────
const CHART_COLORS = [
  C.indigo, C.purple, C.orange, C.green, C.pink,
  C.teal, C.blue, C.red, C.slate, "#EAB308",
  "#EC4899", "#14B8A6", "#F97316", "#A855F7", "#22C55E",
];

const STATUS_COLOR = {
  open: C.indigo, in_progress: C.purple, on_hold: C.orange,
  completed: C.green, approval_pending: C.pink, closed: C.slate, overdue: C.red,
};

const PRIO_COLOR = {
  CRITICAL: "#DC2626", URGENT: "#EA580C", HIGH: "#EAB308",
  MEDIUM: C.green, LOW: C.slate, NORMAL: C.purple,
};

const DL_COLOR = {
  overdue: C.red, due_today: C.orange, due_this_week: C.blue,
  due_next_week: C.teal, on_track: C.green,
};

const AGE_COLOR = {
  "0-7 Days": C.green, "8-14 Days": C.blue,
  "15-30 Days": C.orange, "Over 30 Days": C.red,
  "0-7_days":   C.green, "8-14_days":  C.blue,
  "15-30_days": C.orange, "over_30_days": C.red,
};

const MONTH_ORDER = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const EMPTY_FV = { workspace:[], priority:[], categories:[], department:[], dateFrom:"", dateTo:"" };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function colorize(arr) {
  return arr.map((item, i) => ({ ...item, color: item.color || CHART_COLORS[i % CHART_COLORS.length] }));
}
function sortByMonth(entries) {
  return entries.sort(([a], [b]) => {
    const [aM, aY] = (a+" ").split(" ");
    const [bM, bY] = (b+" ").split(" ");
    if (aY !== bY) return Number(aY) - Number(bY);
    return MONTH_ORDER.indexOf(aM) - MONTH_ORDER.indexOf(bM);
  });
}
function getVal(obj, ...keys) {
  for (const k of keys) { if (obj[k] !== undefined) return Number(obj[k]) || 0; }
  return 0;
}

// ─── Skeleton ────────────────────────────────────────────────────────────────
function Skeleton({ h=20, w="100%", radius=6, mb=0 }) {
  return (
    <div
      className="[animation:skpulse_1.5s_infinite] bg-[linear-gradient(90deg,#f0f0f0_0px,#f8f8f8_40px,#f0f0f0_80px)] bg-[length:200px_100%]"
      style={{ height:h, width:w, borderRadius:radius, marginBottom:mb }}
    />
  );
}
function KpiSkeleton() {
  return (
    <div className="grid gap-3 min-w-[980px] [grid-template-columns:repeat(7,minmax(0,1fr))]">
      {Array.from({length:7}).map((_,i) => (
        <div key={i} className="bg-white rounded-[18px] p-4 border-[1.5px] border-[#F1F5F9]">
          <Skeleton h={9} w="60%" mb={10}/><Skeleton h={32} w="50%" mb={8}/>
          <Skeleton h={8} w="80%" mb={6}/><Skeleton h={4}/>
        </div>
      ))}
    </div>
  );
}
function ChartSkeleton({ h=220 }) {
  return (
    <div className="flex flex-col gap-2.5 p-1">
      <Skeleton h={h-30} radius={8}/>
      <div className="flex gap-2">
        {[40,60,50,70].map((w,i) => <Skeleton key={i} h={8} w={`${w}%`}/>)}
      </div>
    </div>
  );
}

// ─── Staff Search (Admin) — AJAX search like Create Task ─────────────────────
function StaffSearch({ selectedIds, selectedNames, onChange, onNamesChange, showChips=true }) {
  const [query,   setQuery]   = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);
  const ref      = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const search = useCallback((q) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    axiosInstance.get(`/task_assignment/get_users_all?q=${encodeURIComponent(q)}`, {
      signal: abortRef.current.signal,
    })
      .then(res => {
        const users = Array.isArray(res.data) ? res.data : [];
        setResults(users.map(u => ({
          id:    String(u.staffid || u.staff_id || u.id || ""),
          label: (u.display_name || u.alias_name || u.name || "").replace(/\s*\([^)]*@[^)]*\)\s*$/, "").trim() || u.email || "",
          email: u.email || "",
          photo: u.profile_image || u.photo || "",
        })));
        setOpen(true);
      })
      .catch(e => { if (e?.code !== 'ERR_CANCELED' && e?.name !== 'CanceledError') setResults([]); })
      .finally(() => setLoading(false));
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(timerRef.current);
    abortRef.current?.abort();
    timerRef.current = setTimeout(() => search(q), 300);
  };

  const handleFocus = () => { if (results.length === 0) search(""); };

  const toggle = (user) => {
    const already = selectedIds.includes(user.id);
    const newIds   = already ? selectedIds.filter(i => i !== user.id)  : [...selectedIds, user.id];
    const newNames = already ? selectedNames.filter(n => n !== user.label) : [...selectedNames, user.label];
    onChange(newIds);
    onNamesChange(newNames);
  };

  const remove = (id) => {
    const idx  = selectedIds.indexOf(id);
    const newIds   = selectedIds.filter(i => i !== id);
    const newNames = selectedNames.filter((_, i) => i !== idx);
    onChange(newIds);
    onNamesChange(newNames);
  };

  return (
    <div ref={ref} className="relative">
      {/* Search input */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-[10px] bg-white transition-colors border-[1.5px] ${
          open ? "border-[var(--c-indigo)]" : "border-[#E2E8F0]"
        }`}
        style={{ "--c-indigo": C.indigo }}
      >
        {loading ? (
          <div
            className="w-3.5 h-3.5 rounded-full shrink-0 animate-[spin_0.7s_linear_infinite] border-2 border-t-transparent"
            style={{ borderColor: C.indigo, borderTopColor: "transparent" }}
          />
        ) : (
          <Search size={14} color="#94A3B8" className="shrink-0"/>
        )}
        <input value={query} onChange={handleInput} onFocus={handleFocus}
          placeholder="Search staff..."
          className="flex-1 border-none outline-none text-[13px] text-[#1E293B] bg-transparent font-[inherit]"/>
      </div>

      {/* Dropdown results */}
      {open && results.length > 0 && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-[9999] bg-white border-[1.5px] border-[#E2E8F0] rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,.12)] max-h-[220px] overflow-y-auto [scrollbar-width:thin]">
          {results.map(u => {
            const sel = selectedIds.includes(u.id);
            return (
              <div
                key={u.id}
                onClick={() => toggle(u)}
                className={`flex items-center gap-2.5 px-[13px] py-[9px] cursor-pointer transition-colors ${
                  sel ? "bg-[#EEF2FF]" : "bg-transparent hover:bg-[#F8FAFC]"
                }`}
              >
                {/* Avatar */}
                {u.photo ? (
                  <img src={u.photo} alt="" className="w-7 h-7 rounded-full object-cover shrink-0"/>
                ) : (
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold"
                    style={{ background: C.indigo+"22", color: C.indigo }}
                  >
                    {u.label?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1E293B] overflow-hidden whitespace-nowrap text-ellipsis">{u.label}</div>
                  <div className="text-[11px] text-[#94A3B8] overflow-hidden whitespace-nowrap text-ellipsis">{u.email}</div>
                </div>
                <div
                  className={`w-4 h-4 rounded shrink-0 flex items-center justify-center border-[1.5px] ${
                    sel ? "" : "border-[#CBD5E1] bg-white"
                  }`}
                  style={sel ? { borderColor: C.indigo, background: C.indigo } : undefined}
                >
                  {sel && <Check size={10} color="#fff" strokeWidth={3}/>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Selected chips — only shown when showChips=true */}
      {showChips && selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-[5px] mt-[7px]">
          {selectedIds.map((id, idx) => (
            <span key={id} onClick={() => remove(id)}
              className="inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[20px] bg-[#EEF2FF] text-[#4338CA] text-[11px] font-semibold cursor-pointer border border-[#C7D2FE]">
              {selectedNames[idx] || id}
              <X size={10} strokeWidth={2.5}/>
            </span>
          ))}
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── Subordinates Dropdown (Reporting/Product Owner) ─────────────────────────
function SubordinatesDropdown({ selectedIds, onChange, onNamesChange, showChips=true }) {
  const [options, setOptions] = useState([]);
  const [open,    setOpen]    = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    axiosInstance.get("/task_assignment/get_users_subordinates")
      .then(res => {
        const users = Array.isArray(res.data) ? res.data : [];
        setOptions(users.map(u => {
          const rawName   = u.display_name || u.name || "";
          const cleanName = rawName.replace(/\s*\([^)]*@[^)]*\)\s*$/, "").trim() || u.alias_name || u.email || rawName;
          return {
            value: String(u.staffid || u.staff_id || u.id || ""),
            label: cleanName,
            email: "",
          };
        }));
      })
      .catch(err => console.error("Subordinates load failed:", err));
  }, []);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const toggle = (val, lbl) => {
    const already  = selectedIds.includes(val);
    const newIds   = already ? selectedIds.filter(v => v !== val) : [...selectedIds, val];
    const newNames = newIds.map(id => {
      const opt = options.find(o => o.value === id);
      return opt ? opt.label : (id === val ? lbl : id);
    });
    onChange(newIds);
    if (onNamesChange) onNamesChange(newNames);
    setOpen(false);
  };

  const label = selectedIds.length === 0 ? "Choose team member..."
    : selectedIds.length === 1 ? (options.find(o => o.value === selectedIds[0])?.label || selectedIds[0])
    : `${selectedIds.length} selected`;

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen(o=>!o)}
        className={`flex items-center justify-between px-3 py-2 rounded-[10px] cursor-pointer bg-white transition-colors border-[1.5px] ${
          open ? "" : "border-[#E2E8F0]"
        }`}
        style={open ? { borderColor: C.purple } : undefined}
      >
        <span className={`text-[13px] overflow-hidden whitespace-nowrap text-ellipsis flex-1 ${selectedIds.length ? "text-[#1E293B]" : "text-[#94A3B8]"}`}>{label}</span>
        <ChevronDown size={12} color="#94A3B8"
          className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}/>
      </div>

      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-[9999] bg-white border-[1.5px] border-[#E2E8F0] rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,.12)] max-h-[200px] overflow-y-auto [scrollbar-width:thin]">
          {options.length === 0 && (
            <div className="px-[13px] py-2.5 text-xs text-[#94A3B8]">No subordinates found</div>
          )}
          {options.map(opt => {
            const sel = selectedIds.includes(opt.value);
            return (
              <div
                key={opt.value}
                onClick={() => toggle(opt.value, opt.label)}
                className={`flex items-center gap-2.5 px-[13px] py-[9px] cursor-pointer transition-colors ${
                  sel ? "bg-[#F5F3FF]" : "bg-transparent hover:bg-[#F8FAFC]"
                }`}
              >
                {/* Avatar */}
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold"
                  style={{ background: C.purple+"22", color: C.purple }}
                >
                  {opt.label?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1E293B] overflow-hidden whitespace-nowrap text-ellipsis">{opt.label}</div>
                  {opt.email && (
                    <div className="text-[11px] text-[#94A3B8] overflow-hidden whitespace-nowrap text-ellipsis">{opt.email}</div>
                  )}
                </div>
                <div
                  className={`w-4 h-4 rounded shrink-0 flex items-center justify-center border-[1.5px] ${
                    sel ? "" : "border-[#CBD5E1] bg-white"
                  }`}
                  style={sel ? { borderColor: C.purple, background: C.purple } : undefined}
                >
                  {sel && <Check size={10} color="#fff" strokeWidth={3}/>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Selected chips — only shown when showChips=true */}
      {showChips && selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-[5px] mt-[7px]">
          {selectedIds.map(id => {
            const lbl = options.find(o => o.value === id)?.label || id;
            return (
              <span
                key={id}
                onClick={() => toggle(id)}
                className="inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[20px] text-[11px] font-semibold cursor-pointer bg-[#F5F3FF] border"
                style={{ color: C.purple, borderColor: C.purple+"44" }}
              >
                {lbl}<X size={10} strokeWidth={2.5}/>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Role Toggle ─────────────────────────────────────────────────────────────
function RoleToggle({ isAdmin, isReportingPerson, mode, onModeChange }) {
  const hasSubordinates = isReportingPerson;

  const modes = (() => {
    if (isAdmin && hasSubordinates) return [
      { key:"self", label:"My stats",   color:"#534AB7", Icon: User        },
      { key:"team", label:"Team stats", color:"#3B6D11", Icon: Users       },
      { key:"all",  label:"Admin view", color:"#185FA5", Icon: ShieldCheck },
    ];
    if (isAdmin) return [
      { key:"self", label:"My stats",   color:"#534AB7", Icon: User        },
      { key:"all",  label:"Admin view", color:"#185FA5", Icon: ShieldCheck },
    ];
    if (hasSubordinates) return [
      { key:"self", label:"My stats",   color:"#534AB7", Icon: User  },
      { key:"team", label:"Team stats", color:"#3B6D11", Icon: Users },
    ];
    return [];
  })();

  // Only one view available — no toggle needed
  if (modes.length <= 1) return null;

  return (
    <div className="flex items-center bg-[#F1F3F5] border-[0.5px] border-[#DEE2E6] rounded-full p-[3px] gap-0.5 shrink-0">
      {modes.map(m => {
        const active = mode === m.key;
        return (
          <button
            key={m.key}
            onClick={() => onModeChange(m.key)}
            className="flex items-center px-3 py-[5px] rounded-full cursor-pointer border-none text-xs font-semibold whitespace-nowrap transition-all duration-200 gap-[5px]"
            style={{
              background: active ? m.color : "transparent",
              color: active ? "#fff" : "#868E96",
              boxShadow: active ? "0 2px 8px rgba(0,0,0,.2)" : "none",
            }}
          >
            <m.Icon size={13} strokeWidth={2.2} />
            {m.label}
          </button>
        );
      })}
    </div>
  );
}


// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard() {

  // Panel states
  const [filterOpen,          setFilterOpen]          = useState(false);
  // Drilldown states

  const [activeUsersStatus, setActiveUsersStatus] = useState(null); // "active"/"inactive"
  // Generic Task List Detail panel (for chart segment clicks)
  const [taskListPanel, setTaskListPanel] = useState({ open:false, title:"", extraFilter:{} });
  const openTaskList = (title, extraFilter) => setTaskListPanel({ open:true, title, extraFilter });
  const closeTaskList = () => setTaskListPanel(p => ({ ...p, open:false }));

  // Deadline name → API key
  const DL_KEY = { "Overdue":"overdue","Due Today":"due_today","Due This Week":"due_this_week","Due Next Week":"due_next_week","On Track":"on_track" };
  // Age name → API key
  const AGE_KEY = { "0-7_days":"0-7_days","8-14_days":"8-14_days","15-30_days":"15-30_days","over_30_days":"over_30_days","0-7 Days":"0-7_days","8-14 Days":"8-14_days","15-30 Days":"15-30_days","Over 30 Days":"over_30_days" };

  const [inProgressOpen,      setInProgressOpen]      = useState(false);
  const [completedOpen,       setCompletedOpen]       = useState(false);
  const [openTasksOpen,       setOpenTasksOpen]       = useState(false);
  const [onHoldOpen,          setOnHoldOpen]          = useState(false);
  const [pendingApprovalOpen, setPendingApprovalOpen] = useState(false);
  const [overdueOpen,         setOverdueOpen]         = useState(false);
  const [closedTasksOpen,     setClosedTasksOpen]     = useState(false);

  // Filter state
  const [fv,        setFv]        = useState(EMPTY_FV);
  const [appliedFv, setAppliedFv] = useState(EMPTY_FV);
  const [filterOpts, setFilterOpts] = useState({ categories:[], departments:[] });

  // KPI periods
  const [kpiPeriods, setKpiPeriods] = useState(() =>
    Object.fromEntries(KPIS_BASE.map(k => [k.label, "Monthly"]))
  );
  const [kpiRanges, setKpiRanges] = useState(() =>
    Object.fromEntries(KPIS_BASE.map(k => [k.label, { from:"", to:"" }]))
  );

  // Role states
  const [isAdmin,           setIsAdmin]           = useState(false);
  const [isReportingPerson, setIsReportingPerson] = useState(false);
  const [isProductOwner,    setIsProductOwner]    = useState(false);
  const [roleLoaded,        setRoleLoaded]        = useState(false);

  // Toggle mode: "self" | "team" | "all"
  const [viewMode,    setViewMode]    = useState("self");
  const [staffIds,    setStaffIds]    = useState([]);
  const [staffNames,  setStaffNames]  = useState([]);

  // API data
  const [loading,   setLoading]   = useState({ summary:true, charts:true, health:true, metrics:true, dept:true, users:true, stage:true });
  const [summary,   setSummary]   = useState(null);
  const [charts,    setCharts]    = useState(null);
  const [health,    setHealth]    = useState(null);
  const [metrics,   setMetrics]   = useState(null);
  const [deptData,  setDeptData]  = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [stageData, setStageData] = useState(null);

  // Build POST body
  const buildRequestData = useCallback((modeOverride, staffOverride) => {
    const af      = appliedFv;
    const mode    = modeOverride  !== undefined ? modeOverride  : viewMode;
    const sIds    = (staffOverride !== undefined ? staffOverride : staffIds).filter(Boolean);

    let viewType;
    if (mode === "self") {
      viewType = "self";
    } else if (mode === "team") {
      viewType = "team";
    } else if (mode === "all") {
      viewType = sIds.length > 0 ? "specific" : "all";
    } else {
      viewType = mode;
    }

    return {
      view_type:             viewType,
      staff_ids:             sIds,
      product_codes:         af.workspace  || [],
      start_date:            af.dateFrom   || "",
      end_date:              af.dateTo     || "",
      priorities:            af.priority   || [],
      statuses:              [],
      functional_categories: af.categories || [],
      departments:           af.department || [],
    };
  }, [appliedFv, viewMode, staffIds]);

  // Filter dropdown options — load once on mount
  const filterOptsLoaded = useRef(false);
  useEffect(() => {
    if (filterOptsLoaded.current) return;
    filterOptsLoaded.current = true;
    Promise.all([
      axiosInstance.get("/task_assignment/get_functional_categories"),
      axiosInstance.get("/task_assignment/get_departments"),
    ]).then(([catRes, deptRes]) => {
      setFilterOpts({ categories: catRes.data || [], departments: deptRes.data || [] });
    }).catch(err => console.error("Filter opts:", err));
  }, []);

  // Load analytics
  const loadAnalytics = useCallback((adminFlag, modeOverride, staffOverride) => {
    const admin = adminFlag !== undefined ? adminFlag : isAdmin;
    const body  = buildRequestData(modeOverride, staffOverride);
    setLoading({ summary:true, charts:true, health:true, metrics:true, dept:true, users:true, stage:true });

    axiosInstance.post("/task_assignment/get_analytics_summary", body)
      .then(r => r.data?.success && setSummary(r.data))
      .catch(e => console.error("Summary:", e))
      .finally(() => setLoading(l => ({ ...l, summary:false })));

    axiosInstance.post("/task_metrics", body)
      .then(r => r.data?.success && setMetrics(r.data))
      .catch(e => console.error("Metrics:", e))
      .finally(() => setLoading(l => ({ ...l, metrics:false })));

    axiosInstance.post("/task_assignment/get_analytics_charts", body)
      .then(r => r.data?.success && setCharts(r.data))
      .catch(e => console.error("Charts:", e))
      .finally(() => setLoading(l => ({ ...l, charts:false })));

    axiosInstance.post("/task_assignment/get_analytics_health", body)
      .then(r => r.data?.success && setHealth(r.data))
      .catch(e => console.error("Health:", e))
      .finally(() => setLoading(l => ({ ...l, health:false })));

    if (admin) {
      axiosInstance.post("/task_assignment/get_analytics_department", body)
        .then(r => r.data?.success && setDeptData(r.data))
        .catch(e => console.error("Dept:", e))
        .finally(() => setLoading(l => ({ ...l, dept:false })));

      axiosInstance.post("/task_assignment/get_analytics_active_users", body)
        .then(r => r.data?.success && setUsersData(r.data))
        .catch(e => console.error("Users:", e))
        .finally(() => setLoading(l => ({ ...l, users:false })));
    } else {
      setLoading(l => ({ ...l, dept:false, users:false }));
    }

    axiosInstance.post("/task_assignment/get_workspace_stage_distribution", body)
      .then(r => r.data?.success && setStageData(r.data))
      .catch(e => console.error("Stage:", e))
      .finally(() => setLoading(l => ({ ...l, stage:false })));

  }, [buildRequestData, isAdmin]);

  // Role API call on mount — only ONE analytics call here
  useEffect(() => {
    getUserRole()
      .then(res => {
        if (res.data?.success) {
          const admin     = res.data.is_admin            || false;
          const reporting = res.data.is_reporting_person || false;
          const prodOwner = res.data.is_product_owner    || false;
          setIsAdmin(admin);
          setIsReportingPerson(reporting);
          setIsProductOwner(prodOwner);
          const defaultMode = admin ? "all" : reporting ? "team" : "self";
          setViewMode(defaultMode);
          loadAnalytics(admin, defaultMode, []);
        } else {
          loadAnalytics(false, "self", []);
        }
      })
      .catch(err => {
        console.error("Role fetch error:", err);
        loadAnalytics(false, "self", []);
      })
      .finally(() => setRoleLoaded(true));
  }, []); // eslint-disable-line

  // Re-load ONLY when filter applied (not on initial mount)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (roleLoaded) loadAnalytics(isAdmin, viewMode, staffIds);
  }, [appliedFv]); // eslint-disable-line

  // Handle mode change from toggle
  const handleModeChange = (newMode) => {
    setViewMode(newMode);
    setStaffIds([]);
    setStaffNames([]);
    loadAnalytics(isAdmin, newMode, []);
  };

  // Handle staff change — auto reload
  const handleStaffChange = (newIds) => {
    setStaffIds(newIds);
    loadAnalytics(isAdmin, viewMode, newIds);
  };

  // ─── Derived chart data ─────────────────────────────────────────────────────
  const statusD = useMemo(() => {
    const sd = summary?.status_distribution;
    if (!sd) return [];
    return colorize([
      { name:"Open",           value: sd.open             || 0, color: STATUS_COLOR.open },
      { name:"In Progress",    value: sd.in_progress      || 0, color: STATUS_COLOR.in_progress },
      { name:"On Hold",        value: sd.on_hold          || 0, color: STATUS_COLOR.on_hold },
      { name:"Completed",      value: sd.completed        || 0, color: STATUS_COLOR.completed },
      { name:"Pend. Approval", value: sd.approval_pending || 0, color: STATUS_COLOR.approval_pending },
    ].filter(d => d.value > 0));
  }, [summary]);

  const prioD = useMemo(() => {
    const raw = charts?.priority_distribution;
    if (!raw) return [];
    const entries = Array.isArray(raw)
      ? raw.map(i => [i.name || i.priority || "", i.count || i.value || 0])
      : Object.entries(raw);
    return colorize(entries.map(([name, value]) => ({
      name, value: Number(value)||0, color: PRIO_COLOR[String(name).toUpperCase()] || C.slate,
    })).filter(d => d.value > 0));
  }, [charts]);

  const dlD = useMemo(() => {
    const dh = health?.deadline_health;
    if (!dh) return [];
    return colorize([
      { name:"Overdue",       value: dh.overdue       || 0, color: DL_COLOR.overdue },
      { name:"Due Today",     value: dh.due_today     || 0, color: DL_COLOR.due_today },
      { name:"Due This Week", value: dh.due_this_week || 0, color: DL_COLOR.due_this_week },
      { name:"Due Next Week", value: dh.due_next_week || 0, color: DL_COLOR.due_next_week },
      { name:"On Track",      value: dh.on_track      || 0, color: DL_COLOR.on_track },
    ].filter(d => d.value > 0));
  }, [health]);

  const ageD = useMemo(() => {
    const raw = health?.task_aging;
    if (!raw) return [];
    const entries = Array.isArray(raw)
      ? raw.map(i => [i.name || i.age_range || "", i.count || i.value || 0])
      : Object.entries(raw);
    return colorize(entries.map(([name, value]) => ({
      name, value: Number(value)||0, color: AGE_COLOR[name] || C.blue,
    })).filter(d => d.value > 0));
  }, [health]);

  const deptD = useMemo(() => {
    const raw = deptData?.department_distribution;
    if (!raw) return [];
    const items = Array.isArray(raw)
      ? raw.map(i => ({ id: i.id, name: i.name || i.department || "", value: i.count || i.value || 0 }))
      : Object.entries(raw).map(([name, value]) => ({ id: null, name, value: Number(value) || 0 }));
    return items.filter(d => d.value > 0).map((d, i) => ({
      ...d, color: CHART_COLORS[i % CHART_COLORS.length]
    }));
  }, [deptData]);

  const usersD = useMemo(() => {
    const u = usersData?.active_inactive_users;
    if (!u) return [];
    return [
      { name:"Active Users",   value: u.active_users   || u.active   || 0, color: C.green },
      { name:"Inactive Users", value: u.inactive_users || u.inactive || 0, color: C.red },
    ].filter(d => d.value > 0);
  }, [usersData]);

  const wsD = useMemo(() => {
    const raw = charts?.product_analysis;
    if (!raw) return [];
    const arr = Array.isArray(raw)
      ? raw
      : Object.entries(raw).map(([k,v]) => ({ name:k, ...(typeof v==="object"?v:{completed:v}) }));
    return arr.map(item => ({
      ws: item.name || item.product_code || "",
      c:  getVal(item, "completed",   "completed_tasks"),
      p:  getVal(item, "in_progress", "inProgress"),
      o:  getVal(item, "open"),
    }));
  }, [charts]);

  const funcD = useMemo(() => {
    const raw = charts?.functional_categories;
    if (!raw) return [];
    const arr = Array.isArray(raw)
      ? raw
      : Object.entries(raw).map(([k,v]) => ({ name:k, count: typeof v==="number"?v:v?.count||0 }));
    return arr.map(item => ({ n: item.name || item.category || "", v: item.count || item.value || 0 }));
  }, [charts]);

  const kbD = useMemo(() => {
    const dist = stageData?.distribution;
    if (!dist) return [];
    return Object.entries(dist)
      .map(([ws, stages]) => ({
        s: ws,
        v: Object.values(stages).reduce((sum,n) => sum + (Number(n)||0), 0),
      }))
      .filter(r => r.v > 0)
      .sort((a,b) => b.v - a.v);
  }, [stageData]);

  const barD = useMemo(() => {
    const raw = metrics?.progress;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.map(item => ({
        m: item.month || item.label || "",
        c: getVal(item, "completed_tasks", "completed"),
        o: getVal(item, "overdue_tasks",   "overdue"),
      }));
    }
    return sortByMonth(Object.entries(raw)).map(([m,v]) => ({
      m,
      c: typeof v==="object" ? getVal(v,"completed_tasks","completed") : 0,
      o: typeof v==="object" ? getVal(v,"overdue_tasks","overdue")     : 0,
    }));
  }, [metrics]);

  const effD = useMemo(() => {
    const raw = metrics?.efficiency;
    if (!raw) return [];
    if (!Array.isArray(raw) && typeof raw==="object") {
      return sortByMonth(Object.entries(raw).filter(([k]) => !k.toLowerCase().includes("overall")))
        .map(([m,v]) => ({ m, v: typeof v==="object" ? v.pct||v.value||0 : Number(v)||0 }));
    }
    if (Array.isArray(raw)) return raw.map(i => ({ m: i.month||i.label||"", v: i.pct||i.value||0 }));
    if (typeof raw==="string") {
      const str = raw.replace(/[{}]/g,"");
      const entries = [];
      const regex = /([A-Za-z]+)\s+(\d+)\s*[-:]\s*(\d+)/g;
      let match;
      while ((match = regex.exec(str)) !== null) {
        const [,mon,yr,val] = match;
        if (mon.toLowerCase()==="overall") continue;
        const monCap = mon.charAt(0).toUpperCase()+mon.slice(1).toLowerCase();
        const fullYear = yr.length===2 ? "20"+yr : yr;
        entries.push({ m:`${monCap} ${fullYear}`, mon:monCap, yr:Number(fullYear), v:Number(val) });
      }
      return entries.sort((a,b) => a.yr!==b.yr ? a.yr-b.yr : MONTH_ORDER.indexOf(a.mon)-MONTH_ORDER.indexOf(b.mon));
    }
    return [];
  }, [metrics]);

  const trendD = useMemo(() => {
    const raw = charts?.completion_trends;
    if (!raw) return [];
    const arr = Array.isArray(raw)
      ? raw
      : Object.entries(raw).map(([k,v]) => ({ date:k, completed: typeof v==="number"?v:v?.completed||0 }));
    return arr.map(item => ({
      d: item.date || item.label || item.month || "",
      v: item.completed ?? item.rate ?? item.value ?? item.completion_rate ?? 0,
    }));
  }, [charts]);

  const efficiencyValue = useMemo(() => {
    if (!metrics) return 0;
    const raw = metrics.total_efficiency || metrics.overall_efficiency || 0;
    return Math.round(typeof raw==="string" ? parseFloat(raw) : raw);
  }, [metrics]);

  const progComp = summary?.status_distribution?.completed || 0;
  const progTot  = summary?.total_tasks || 0;
  const progPct  = progTot > 0 ? Math.round((progComp/progTot)*100) : 0;

  // Current filter body for drilldown panels
  const drillFilterBody = useMemo(() => buildRequestData(), [buildRequestData]);

  const kpiValues = useMemo(() => {
    const sd = summary?.status_distribution || {};
    return {
      "Open Tasks":       sd.open             || 0,
      "In Progress":      sd.in_progress      || 0,
      "On Hold":          sd.on_hold          || 0,
      "Completed":        sd.completed        || 0,
      "Pending Approval": sd.approval_pending || 0,
      "Overdue":          sd.overdue          || 0,
      "Closed Tasks":     sd.closed           || 0,
    };
  }, [summary]);

  // Active filter chips
  const activeChips = useMemo(() => {
    const af = appliedFv;
    const chips = [];
    if (af.workspace?.length)  chips.push({ key:"workspace",  label:`WS: ${af.workspace.join(", ")}` });
    if (af.priority?.length)   chips.push({ key:"priority",   label:`Priority: ${af.priority.join(", ")}` });
    if (af.categories?.length) chips.push({ key:"categories", label:`Cat: ${af.categories.join(", ")}` });
    if (af.department?.length) {
      const deptLabels = af.department.map(id => {
        const dept = (filterOpts.departments || []).find(d => String(d.id || d.name) === String(id));
        return dept?.name || id;
      });
      chips.push({ key:"department", label:`Dept: ${deptLabels.join(", ")}` });
    }
    if (af.dateFrom)           chips.push({ key:"dateFrom",   label:`From: ${af.dateFrom}` });
    if (af.dateTo)             chips.push({ key:"dateTo",     label:`To: ${af.dateTo}` });
    return chips;
  }, [appliedFv, filterOpts]);

  const removeChip = (key) => {
    const cleared = { ...appliedFv, [key]: Array.isArray(appliedFv[key]) ? [] : "" };
    setAppliedFv(cleared); setFv(cleared);
  };

  const statusTotal = statusD.reduce((s,d) => s+d.value, 0);
  const usersTotal  = usersD.reduce((s,d)  => s+d.value, 0);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="rounded-lg flex bg-[#f6f6f6] min-h-screen font-['Inter',-apple-system,sans-serif] text-[13px] overflow-x-hidden relative">
      <style>{`
        @keyframes skpulse{0%{background-position:-200px 0}100%{background-position:calc(200px + 100%) 0}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      <div className="flex-1 px-6 pt-5 pb-8 min-w-0 overflow-x-hidden">

        {/* ── Header row: [search/dropdown] [chips...] [toggle] [filter] ── */}
        <div className="flex items-center gap-2.5 mb-4 flex-wrap">

          {/* Search/Dropdown — fixed width */}
          {viewMode === "all" && (
            <div className="w-[260px] shrink-0">
              <StaffSearch
                selectedIds={staffIds}
                selectedNames={staffNames}
                onChange={handleStaffChange}
                onNamesChange={setStaffNames}
                showChips={false}
              />
            </div>
          )}
          {viewMode === "team" && (
            <div className="w-[240px] shrink-0">
              <SubordinatesDropdown
                selectedIds={staffIds}
                onChange={handleStaffChange}
                onNamesChange={setStaffNames}
                showChips={false}
              />
            </div>
          )}

          {/* Chips — staff + filter — inline, flex grow */}
          <div className="flex flex-wrap gap-1.5 flex-1 items-center min-w-0">

            {/* Staff chips */}
            {staffIds.map((id, idx) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[20px] shrink-0 text-[11px] font-semibold border"
                style={{
                  background: viewMode==="team" ? "#F5F3FF" : "#EEF2FF",
                  color: viewMode==="team" ? C.purple : C.indigo,
                  borderColor: viewMode==="team" ? C.purple+"44" : "#C7D2FE",
                }}
              >
                {staffNames[idx] || id}
                <span
                  onClick={() => {
                    const newIds   = staffIds.filter((_,i)=>i!==idx);
                    const newNames = staffNames.filter((_,i)=>i!==idx);
                    setStaffIds(newIds); setStaffNames(newNames);
                    loadAnalytics(isAdmin, viewMode, newIds);
                  }}
                  className="cursor-pointer leading-none text-[13px] font-bold"
                  style={{ color: viewMode==="team" ? C.purple : C.indigo }}
                >×</span>
              </span>
            ))}

            {/* Filter chips */}
            {activeChips.map(chip => (
              <span key={chip.key} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[20px] shrink-0 bg-[#EEF2FF] text-[#4338CA] text-[11px] font-semibold border border-[#C7D2FE]">
                {chip.label}
                <span onClick={() => removeChip(chip.key)}
                  className="cursor-pointer leading-none text-[13px] font-bold text-[#6366F1]">×</span>
              </span>
            ))}

            {/* Clear All */}
            {(staffIds.length > 0 || activeChips.length > 0) && (
              <button
                onClick={() => {
                  setAppliedFv(EMPTY_FV); setFv(EMPTY_FV);
                  setStaffIds([]); setStaffNames([]);
                  loadAnalytics(isAdmin, viewMode, []);
                }}
                className="px-2.5 py-1 rounded-[20px] bg-[#FEF2F2] text-[#EF4444] text-[11px] font-semibold border border-[#FCA5A5] cursor-pointer shrink-0"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Right — Toggle + Filter — always right */}
          <div className="flex items-center gap-2.5 shrink-0 ml-auto">
            <RoleToggle
              isAdmin={isAdmin}
              isReportingPerson={isReportingPerson}
              isProductOwner={isProductOwner}
              mode={viewMode}
              onModeChange={handleModeChange}
              staffIds={staffIds}
              staffNames={staffNames}
              onStaffChange={handleStaffChange}
              onStaffNamesChange={setStaffNames}
            />
            <button
              onClick={() => setFilterOpen(o=>!o)}
              className={`flex items-center gap-[7px] text-white border-none px-[18px] py-[9px] rounded-[10px] text-[13px] font-semibold cursor-pointer shadow-[0_2px_8px_rgba(99,102,241,.35)] relative ${
                filterOpen ? "bg-[#4F46E5]" : "bg-[#6366F1]"
              }`}
            >
              <SlidersHorizontal size={14}/> Filter
              {(activeChips.length + staffIds.length) > 0 && (
                <span className="absolute -top-[7px] -right-[7px] w-[18px] h-[18px] rounded-full bg-[#EF4444] text-white text-[10px] font-extrabold flex items-center justify-center">
                  {activeChips.length + staffIds.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="overflow-x-auto mb-5 pb-1 [scrollbar-width:thin]">
          {loading.summary ? <KpiSkeleton/> : (
            <div className="grid gap-3 min-w-[980px] [grid-template-columns:repeat(7,minmax(0,1fr))]">
              {KPIS_BASE.map(k => (
                <KPICard key={k.label} {...k}
                  val={kpiValues[k.label] ?? k.val}
                  filterRatio={1}
                  period={kpiPeriods[k.label]}
                  onPeriodChange={(l,v) => setKpiPeriods(prev=>({...prev,[l]:v}))}
                  cRange={kpiRanges[k.label]}
                  onRangeChange={(l,f,v) => setKpiRanges(prev=>({...prev,[l]:{...prev[l],[f]:v}}))}
                  onCountClick={
                    k.label==="Open Tasks"       ? ()=>setOpenTasksOpen(true)       :
                    k.label==="In Progress"      ? ()=>setInProgressOpen(true)      :
                    k.label==="On Hold"          ? ()=>setOnHoldOpen(true)          :
                    k.label==="Completed"        ? ()=>setCompletedOpen(true)       :
                    k.label==="Pending Approval" ? ()=>setPendingApprovalOpen(true) :
                    k.label==="Overdue"          ? ()=>setOverdueOpen(true)         :
                    k.label==="Closed Tasks"     ? ()=>setClosedTasksOpen(true)     : undefined
                  }/>
              ))}
            </div>
          )}
        </div>

        {/* Row 1 — Efficiency / Monthly Task Progress / Monthly Efficiency */}
        <div className="dash-row3">
          <Card title="Efficiency" Icon={Zap} iBg="#EEF2FF" iC={C.indigo}
            badge={<Bdg t={`${efficiencyValue}% Overall`} bg="#FEF9C3" clr="#854D0E"/>}>
            {loading.metrics ? <ChartSkeleton h={200}/> : <GaugeSVG value={efficiencyValue}/>}
          </Card>

          <Card title="Monthly Task Progress" Icon={BarChart2} iBg="#EFF6FF" iC={C.blue}>
            {loading.metrics ? <ChartSkeleton h={200}/> : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={barD} barSize={8} margin={{top:4,right:4,left:-24,bottom:24}}>
                  <CartesianGrid vertical={false} stroke="#F1F5F9" strokeDasharray="3 3"/>
                  <XAxis dataKey="m" tick={{...aT,fontSize:7.5}} angle={-35} textAnchor="end" tickLine={false} axisLine={false}/>
                  <YAxis tick={aT} tickLine={false} axisLine={false}/>
                  <Tooltip content={<ChartTooltip/>}/>
                  <Bar dataKey="c" name="Completed" fill={C.green}  radius={[3,3,0,0]}/>
                  <Bar dataKey="o" name="Overdue"   fill="#FCA5A5" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card title="Monthly Efficiency" Icon={TrendingUp} iBg="#ECFDF5" iC={C.green}>
            {loading.metrics ? <ChartSkeleton h={200}/> : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={effD} margin={{top:4,right:4,left:-24,bottom:24}}>
                  <defs><linearGradient id="eg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.teal} stopOpacity={.2}/>
                    <stop offset="95%" stopColor={C.teal} stopOpacity={0}/>
                  </linearGradient></defs>
                  <CartesianGrid vertical={false} stroke="#F1F5F9" strokeDasharray="3 3"/>
                  <XAxis dataKey="m" tick={{...aT,fontSize:7.5}} angle={-35} textAnchor="end" tickLine={false} axisLine={false}/>
                  <YAxis tick={aT} tickLine={false} axisLine={false} domain={[0,100]}/>
                  <Tooltip content={<ChartTooltip/>}/>
                  <Area type="monotone" dataKey="v" name="Efficiency %" stroke={C.teal} strokeWidth={2.5}
                    fill="url(#eg)" dot={{r:3.5,fill:C.teal,stroke:"#fff",strokeWidth:2}}/>
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>

        {/* Row 2 — Status / Priority / Trends */}
        <div className="dash-row3">
          <Card title="Task Status Overview" Icon={LuPie} iBg="#F5F3FF" iC={C.purple}>
            {loading.summary ? <ChartSkeleton h={200}/> : (
              <>
                <Donut data={statusD} ir={52} or={74} center={{val:statusTotal.toLocaleString(),lbl:"Total Tasks"}} height={155}
                  onSegmentClick={(d) => {
                    const s = (d.name||"").toLowerCase();
                    if (s==="open") setOpenTasksOpen(true);
                    else if (s==="in progress") setInProgressOpen(true);
                    else if (s==="on hold") setOnHoldOpen(true);
                    else if (s==="completed") setCompletedOpen(true);
                    else if (s.includes("approval")) setPendingApprovalOpen(true);
                  }}/>
                <Leg data={statusD}/>
              </>
            )}
          </Card>

          <Card title="Priority Breakdown" Icon={Flag} iBg="#FEF2F2" iC={C.red}>
            {loading.charts ? <ChartSkeleton h={200}/> : (
              <>
                <Donut data={prioD} ir={48} or={70} height={155}
                  onSegmentClick={(d) => openTaskList(`${d.name} Priority Tasks`, { priorities:[d.name] })}/>
                <Leg data={prioD} onClickItem={(name) => openTaskList(`${name} Priority Tasks`, { priorities:[name] })}/>
              </>
            )}
          </Card>

          <Card title="Completion Trends" Icon={Activity} iBg="#ECFDF5" iC={C.green}>
            {loading.charts ? <ChartSkeleton h={200}/> : (
              <ResponsiveContainer width="100%" height={188}>
                <AreaChart data={trendD} margin={{top:4,right:4,left:-24,bottom:4}}>
                  <defs><linearGradient id="tg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.green} stopOpacity={.2}/>
                    <stop offset="95%" stopColor={C.green} stopOpacity={0}/>
                  </linearGradient></defs>
                  <CartesianGrid vertical={false} stroke="#F1F5F9" strokeDasharray="3 3"/>
                  <XAxis dataKey="d" tick={aT} tickLine={false} axisLine={false}/>
                  <YAxis tick={aT} tickLine={false} axisLine={false}/>
                  <Tooltip content={<ChartTooltip/>}/>
                  <Area type="monotone" dataKey="v" name="Completion Rate" stroke={C.green} strokeWidth={2.5}
                    fill="url(#tg)" dot={{r:4,fill:C.green,stroke:"#fff",strokeWidth:2}}/>
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>

        {/* Row 3 — Workspace / Functional / Kanban */}
        <div className="dash-row3">
          <Card title="Workspace-wise Analysis" Icon={Layers} iBg="#EFF6FF" iC={C.blue}
            badge={<Bdg t={`${wsD.length} workspaces`} bg="#EFF6FF" clr={C.blue}/>}>
            {loading.charts ? <ChartSkeleton h={280}/> : (
              <>
                <MiniLeg items={[{c:C.green,l:"Completed"},{c:C.purple,l:"In Progress"},{c:"#FCA5A5",l:"Open"}]}/>
                <ScrollChart data={wsD} itemH={32} maxH={258}>
                  <BarChart layout="vertical" data={wsD} barSize={7} margin={{top:0,right:12,left:0,bottom:0}}>
                    <CartesianGrid horizontal={false} stroke="#F1F5F9" strokeDasharray="3 3"/>
                    <XAxis type="number" tick={{...aT,fontSize:8.5}} tickLine={false} axisLine={false}/>
                    <YAxis type="category" dataKey="ws" tick={{...aT,fontSize:8.5}} tickLine={false} axisLine={false} width={88}/>
                    <Tooltip content={<ChartTooltip/>}/>
                    <Bar dataKey="c" name="Completed"   fill={C.green}  stackId="a" onClick={(d)=>openTaskList(`${d.ws} — Completed`, { product_name:d.ws, status:"completed" })} style={{cursor:"pointer"}}/>
                    <Bar dataKey="p" name="In Progress" fill={C.purple} stackId="a" onClick={(d)=>openTaskList(`${d.ws} — In Progress`, { product_name:d.ws, status:"in progress" })} style={{cursor:"pointer"}}/>
                    <Bar dataKey="o" name="Open"        fill="#FCA5A5"  stackId="a" radius={[0,4,4,0]} onClick={(d)=>openTaskList(`${d.ws} — Open`, { product_name:d.ws, status:"open" })} style={{cursor:"pointer"}}/>
                  </BarChart>
                </ScrollChart>
              </>
            )}
          </Card>

          <Card title="All Functional Areas" Icon={LayoutGrid} iBg="#FFFBEB" iC={C.orange}
            badge={<Bdg t={`${funcD.length} areas`} bg="#FFFBEB" clr={C.orange}/>}>
            {loading.charts ? <ChartSkeleton h={280}/> : (
              <ScrollChart data={funcD} itemH={30} maxH={280}>
                <BarChart layout="vertical" data={funcD} barSize={8} margin={{top:0,right:12,left:0,bottom:0}}>
                  <defs><linearGradient id="fg" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%"   stopColor={C.indigo} stopOpacity={.75}/>
                    <stop offset="100%" stopColor={C.purple}/>
                  </linearGradient></defs>
                  <CartesianGrid horizontal={false} stroke="#F1F5F9" strokeDasharray="3 3"/>
                  <XAxis type="number" tick={{...aT,fontSize:8.5}} tickLine={false} axisLine={false}/>
                  <YAxis type="category" dataKey="n" tick={{...aT,fontSize:8.5}} tickLine={false} axisLine={false} width={84}/>
                  <Tooltip content={<ChartTooltip/>}/>
                  <Bar dataKey="v" name="Tasks" fill="url(#fg)" radius={[0,5,5,0]}
                    onClick={(d)=>openTaskList(`${d.n} — Tasks`, { functional_categories:[d.n] })} style={{cursor:"pointer"}}/>
                </BarChart>
              </ScrollChart>
            )}
          </Card>

          <Card title="Kanban — Stage Distribution" Icon={LayoutDashboard} iBg="#F0FDF4" iC={C.green}
            badge={<Bdg t={`${kbD.length} workspaces`} bg="#F0FDF4" clr={C.green}/>}>
            {loading.stage ? <ChartSkeleton h={280}/> : (
              <ScrollChart data={kbD} itemH={32} maxH={280}>
                <BarChart layout="vertical" data={kbD} barSize={8} margin={{top:0,right:12,left:0,bottom:0}}>
                  <defs><linearGradient id="kg" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%"   stopColor={C.indigo} stopOpacity={.8}/>
                    <stop offset="100%" stopColor={C.teal}/>
                  </linearGradient></defs>
                  <CartesianGrid horizontal={false} stroke="#F1F5F9" strokeDasharray="3 3"/>
                  <XAxis type="number" tick={{...aT,fontSize:8.5}} tickLine={false} axisLine={false}/>
                  <YAxis type="category" dataKey="s" tick={{...aT,fontSize:8.5}} tickLine={false} axisLine={false} width={110}/>
                  <Tooltip content={<ChartTooltip/>}/>
                  <Bar dataKey="v" name="Tasks in Kanban" fill="url(#kg)" radius={[0,5,5,0]}
                    onClick={(d)=>openTaskList(`${d.s} — Stage Tasks`, { product_name:d.s, require_stage: true })} style={{cursor:"pointer"}}/>
                </BarChart>
              </ScrollChart>
            )}
          </Card>
        </div>

        {/* Row 4 — Deadline / Age / Progress */}
        <div className="dash-row3">
          <Card title="Deadline Health" Icon={CalendarCheck} iBg="#FFF1F2" iC={C.red}>
            {loading.health ? <ChartSkeleton h={200}/> : (
              <>
                <Donut data={dlD} ir={52} or={74}
                  center={dlD[0] ? {val:dlD[0].value.toLocaleString(),lbl:"Overdue",color:C.red} : undefined}
                  height={155}
                  onSegmentClick={(d) => openTaskList(`${d.name} Tasks`, { deadline_type: DL_KEY[d.name] || d.name.toLowerCase().replace(/ /g,"_") })}/>
                <Leg data={dlD} onClickItem={(name) => openTaskList(`${name} Tasks`, { deadline_type: DL_KEY[name] || name.toLowerCase().replace(/ /g,"_") })}/>
              </>
            )}
          </Card>

          <Card title="Task Age Distribution" Icon={Timer} iBg="#EFF6FF" iC={C.blue}>
            {loading.health ? <ChartSkeleton h={200}/> : (
              <>
                <Donut data={ageD} ir={48} or={74} height={155}
                  onSegmentClick={(d) => openTaskList(`${d.name} Old Tasks`, { age_range: AGE_KEY[d.name] || d.name })}/>
                <Leg data={ageD} onClickItem={(name) => openTaskList(`${name} Old Tasks`, { age_range: AGE_KEY[name] || name })}/>
              </>
            )}
          </Card>

          <Card title="Overall Progress" Icon={Target} iBg="#ECFDF5" iC={C.green}
            badge={<Bdg t={`${progComp.toLocaleString()}/${progTot.toLocaleString()}`} bg="#EEF2FF" clr="#4338CA"/>}>
            {loading.summary ? <ChartSkeleton h={220}/> : (
              <>
                <Donut data={[
                  {name:"Completed", value:progComp,                        color:C.green},
                  {name:"Remaining", value:Math.max(0, progTot - progComp), color:"#E2E8F0"},
                ]} ir={58} or={78} center={{val:`${progPct}%`,lbl:"of tasks",color:C.green}} height={200}
                  onSegmentClick={(d) => {
                    if (d.name==="Completed") openTaskList("Completed Tasks", { status:"completed" });
                    else openTaskList("Pending Tasks", { statuses:["open","in progress","on hold","approval pending"] });
                  }}/>
                <div className="text-center mt-2 text-[11px] text-[#94A3B8]">
                  <span className="font-bold" style={{ color:C.green }}>{progComp.toLocaleString()}</span>
                  {" of "}<strong>{progTot.toLocaleString()}</strong> tasks completed
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Row 5 — Dept / Users (admin only) */}
        {isAdmin && (
          <div className="dash-row2">
            <Card title="Department-wise Distribution" Icon={Building2} iBg="#F5F3FF" iC={C.purple}>
              {loading.dept ? <ChartSkeleton h={240}/> : (
                <>
                  <Donut data={deptD} ir={62} or={88} height={190}
                    onSegmentClick={(d) => openTaskList(`${d.name} — Tasks`, d.id ? { department_id:d.id } : { department_name:d.name })}/>
                  <Leg data={deptD} onClickItem={(name) => {
                    const found = deptD.find(x => x.name === name);
                    openTaskList(`${name} — Tasks`, found?.id ? { department_id:found.id } : { department_name:name });
                  }}/>
                </>
              )}
            </Card>

            <Card title="Active vs Inactive Users" Icon={Users} iBg="#ECFDF5" iC={C.green}>
              {loading.users ? <ChartSkeleton h={240}/> : (
                <>
                  <Donut data={usersD} ir={65} or={90} center={{val:usersTotal.toLocaleString(),lbl:"Total Users"}} height={220}
                    onSegmentClick={(d) => setActiveUsersStatus(d.name==="Active Users"?"active":"inactive")}/>
                  <div className="flex justify-center gap-10 mt-3.5">
                    {usersD.map(d => (
                      <div key={d.name} className="text-center">
                        <div className="text-[30px] font-extrabold leading-none" style={{ color:d.color }}>{d.value.toLocaleString()}</div>
                        <div className="text-[11px] text-[#64748B] mt-1 font-medium">{d.name}</div>
                        <div className="text-[10px] text-[#94A3B8]">
                          ({usersTotal>0 ? ((d.value/usersTotal)*100).toFixed(1) : 0}%)
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Status Panels — with real API filterBody */}
      <OpenTasksPanel       open={openTasksOpen}       onClose={()=>setOpenTasksOpen(false)}       filterBody={drillFilterBody}/>
      <InProgressPanel      open={inProgressOpen}      onClose={()=>setInProgressOpen(false)}      filterBody={drillFilterBody}/>
      <OnHoldPanel          open={onHoldOpen}           onClose={()=>setOnHoldOpen(false)}          filterBody={drillFilterBody}/>
      <CompletedPanel       open={completedOpen}        onClose={()=>setCompletedOpen(false)}       filterBody={drillFilterBody}/>
      <PendingApprovalPanel open={pendingApprovalOpen}  onClose={()=>setPendingApprovalOpen(false)} filterBody={drillFilterBody}/>
      <OverduePanel         open={overdueOpen}          onClose={()=>setOverdueOpen(false)}         filterBody={drillFilterBody}/>
      <ClosedTasksPanel     open={closedTasksOpen}      onClose={()=>setClosedTasksOpen(false)}     filterBody={drillFilterBody}/>

      {/* Generic Task List Detail Panel — chart segment clicks */}
      <TaskListDetailPanel
        open={taskListPanel.open}
        onClose={closeTaskList}
        title={taskListPanel.title}
        filterBody={drillFilterBody}
        extraFilter={taskListPanel.extraFilter}
      />

      {/* Active / Inactive Users Panel */}
      <ActiveUsersPanel
        open={!!activeUsersStatus}
        status={activeUsersStatus}
        filterBody={drillFilterBody}
        onClose={() => setActiveUsersStatus(null)}
      />

      {/* Filter Sidebar */}
      <FilterSidebar
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={() => { setAppliedFv({...fv}); setFilterOpen(false); }}
        onClear={() => { setFv(EMPTY_FV); setAppliedFv(EMPTY_FV); }}
        fv={fv} setFv={setFv}
        isAdmin={isAdmin}
        isReportingPerson={isReportingPerson}
        isProductOwner={isProductOwner}
        filterOpts={filterOpts}
      />
    </div>
  );
}